import type { RecordsTableConfig } from '@cims-online/shared'
import type { ExpandableConfig } from 'antd/lib/table/interface'
import type { CIMSBasicFieldProps } from 'components/CIMSBasicFields/types'
import { createElement } from 'react'
import { HelpMessage } from 'utils/constants'
import type { WorkflowRecord } from '../../types'
import Comments from './components/Comments'
import ExpandedRecord from './components/ExpandedRecord'
import type { ExpandedRecordSection } from './components/ExpandedRecord/types'
import Notes, { type ApprovalNote } from './components/Notes'
import { FieldValue, findFieldValue } from './fieldValues'
import { fieldValueText, renderFieldValue } from './renderFieldValue'
import type { CommentActionContext } from './types'

function expandedFieldProperty(fieldValue: FieldValue) {
  return fieldValue.value.type === 'generated_int' ? 'formattedValue' : 'value'
}

function hasDisplayableValue(fieldValue: FieldValue): boolean {
  return !!fieldValueText(
    fieldValue.value,
    expandedFieldProperty(fieldValue)
  ).trim()
}

function expandedField(
  fieldValue: FieldValue
): CIMSBasicFieldProps | undefined {
  if (!hasDisplayableValue(fieldValue)) return
  if (fieldValue.value.type === 'pay_items')
    return {
      type: 'payItems',
      object: 'Technical Direction',
      payItems: fieldValue.value.value.map(
        ({ payItemDeleted, payItemId, payItemLabel }) => ({
          deleted: payItemDeleted,
          globalId: payItemId,
          label: payItemLabel,
        })
      ),
    }

  return {
    label: fieldValue.name,
    children: renderFieldValue(
      fieldValue.value,
      expandedFieldProperty(fieldValue)
    ),
  }
}

function expandedFields(fieldIds: string[], record: WorkflowRecord) {
  const fields = fieldIds.reduce<CIMSBasicFieldProps[]>((fields, fieldId) => {
    const found = findFieldValue(record, fieldId)
    if (!found) return fields
    const field = expandedField(found)
    if (field) fields.push(field)
    return fields
  }, [])
  const notes = approvalNotes(record)
  if (notes.length)
    fields.push({
      label: 'Notes',
      help: HelpMessage.CustomWorkflows.Records.Notes,
      children: createElement(Notes, { notes }),
    })
  return fields
}

function approvalNotes(record: WorkflowRecord): ApprovalNote[] {
  return Object.values(record.values).flatMap((fieldValue) => {
    if (fieldValue.type !== 'approvals') return []
    return fieldValue.value.flatMap(({ optionLabel, state }) => {
      if (!state?.note?.trim()) return []
      return {
        approvalGroup: optionLabel,
        date: state.date,
        disposition: state.type,
        text: state.note,
        userMail: state.userMail,
        userName: state.userUsername,
      }
    })
  })
}

function commentsForField(record: WorkflowRecord, fieldId: string) {
  const fieldValue = findFieldValue(record, fieldId)
  if (!fieldValue) return []
  if (fieldValue.value.type !== 'comments')
    throw new Error(
      `configured comments field ${fieldId} resolved to ${fieldValue.value.type}`
    )
  return fieldValue.value.value
}

function commentSections(
  commentFields: RecordsTableConfig['expand']['commentFields'],
  record: WorkflowRecord,
  commentActions: CommentActionContext
): ExpandedRecordSection[] {
  return commentFields.map(({ fieldId, name, rowCreate }) => ({
    key: fieldId,
    children: createElement(Comments, {
      ...commentActions,
      comments: commentsForField(record, fieldId),
      fieldId,
      name,
      recordId: record.id,
      rowCreate,
    }),
  }))
}

/**
 * Builds the expandable section in the field order supplied by the server.
 *
 * Field names come from the transformed values for labels. Pay items use the
 * existing specialized basic-field renderer; other field types share the table
 * value renderer.
 */
export function expandOptions(
  { commentFields, fieldIds }: RecordsTableConfig['expand'],
  commentActions: CommentActionContext
): ExpandableConfig<WorkflowRecord> {
  return {
    expandedRowClassName: () => 'records-table-expanded',
    expandedRowRender: (record) => {
      const additionalInfo = expandedFields(fieldIds, record)
      return createElement(ExpandedRecord, {
        additionalInfo: additionalInfo.length ? additionalInfo : undefined,
        sections: commentSections(commentFields, record, commentActions),
      })
    },
    rowExpandable: (record) =>
      commentFields.length > 0 ||
      approvalNotes(record).length > 0 ||
      fieldIds.some((fieldId) => {
        const fieldValue = findFieldValue(record, fieldId)
        return !!fieldValue && hasDisplayableValue(fieldValue)
      }),
  }
}
